import React, { useState, useCallback } from 'react';
import {
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  createStyles,
  Grid,
  IconButton,
  makeStyles,
  Theme,
  Typography,
} from '@material-ui/core';
import DeleteIcon from '@material-ui/icons/Delete';
import StarIcon from '@material-ui/icons/Star';
import ConfirmationNumberIcon from '@material-ui/icons/ConfirmationNumber';
import { SeasonViewQuery, useDeletePrizeMutation, SeasonViewDocument } from '../../../../../schema';
import { PrizeModal, DeletePrizeModal } from './PrizeModal';
import { Prize } from './PrizeModal/types';
import { toast } from 'react-toastify';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    prizeCard: {
      display: 'flex',
      flexDirection: 'column',
      width: '250px',
      height: '100%',
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      border: `1px solid ${theme.palette.divider}`,
      borderRadius: theme.shape.borderRadius,
      overflow: 'hidden',
      backgroundColor: theme.palette.background.paper,
      '&:hover': {
        boxShadow: theme.shadows[4],
        borderColor: theme.palette.primary.light,
      },
    },
    prizeImageWrapper: {
      position: 'relative',
      width: '100%',
      paddingTop: '60%',
      backgroundColor: theme.palette.grey[100],
      overflow: 'hidden',
    },
    prizeImage: {
      position: 'absolute',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      objectFit: 'cover',
      borderRadius: 0,
    },
    prizeHeader: {
      position: 'absolute',
      top: theme.spacing(1),
      left: theme.spacing(1),
      right: theme.spacing(1),
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'flex-start',
    },
    prizePrice: {
      backgroundColor: theme.palette.primary.main,
      color: theme.palette.common.white,
      padding: theme.spacing(0.5, 1.5),
      borderRadius: 16,
      fontWeight: 600,
      fontSize: '0.85rem',
    },
    deleteButton: {
      backgroundColor: 'rgba(255,255,255,0.9)',
      padding: 4,
      '&:hover': {
        boxShadow: theme.shadows[4],
      },
    },
    prizeContent: {
      display: 'flex',
      flexDirection: 'column',
      flex: 1,
      padding: theme.spacing(1.5),
    },
    prizeTitle: {
      fontWeight: 600,
      fontSize: '0.95rem',
      lineHeight: 1.4,
      marginBottom: theme.spacing(0.5),
      color: theme.palette.text.primary,
    },
    prizeDescription: {
      fontSize: '0.8rem',
      color: theme.palette.text.secondary,
      lineHeight: 1.4,
      marginBottom: theme.spacing(1),
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      display: '-webkit-box',
      '-webkit-line-clamp': 2,
      '-webkit-box-orient': 'vertical',
    },
    chipContainer: {
      display: 'flex',
      gap: '5px',
      flexWrap: 'wrap',
      marginTop: 'auto',
    },
    premiumChip: {
      height: 22,
      fontSize: '0.7rem',
      fontWeight: 500,
      backgroundColor: '#FFF8E1',
      color: '#F57C00',
      border: '1px solid #FFE082',
      '& .MuiChip-icon': {
        fontSize: '0.85rem',
        color: '#F57C00',
      },
    },
    promoChip: {
      height: 22,
      fontSize: '0.7rem',
      fontWeight: 500,
      backgroundColor: '#E8F5E9',
      color: '#2E7D32',
      border: '1px solid #A5D6A7',
      '& .MuiChip-icon': {
        fontSize: '0.85rem',
        color: '#2E7D32',
      },
    },
    promoCode: {
      fontSize: '0.75rem',
      color: theme.palette.text.secondary,
      marginTop: theme.spacing(0.5),
      '& strong': {
        color: theme.palette.text.primary,
        fontFamily: 'monospace',
      },
    },
    emptyMessage: {
      textAlign: 'center',
      color: theme.palette.text.secondary,
      padding: theme.spacing(4),
    },
    headerActions: {
      display: 'flex',
      gap: '10px',
      alignItems: 'center',
    },
  })
);

type Props = {
  data: SeasonViewQuery | undefined;
  seasonId: string;
};

export const PrizesList = ({ data, seasonId }: Props) => {
  const classes = useStyles();
  const prizes = data?.season?.prizes?.nodes || [];

  const [deletePrize, { loading: deleteLoading }] = useDeletePrizeMutation({
    refetchQueries: [{ query: SeasonViewDocument, variables: { id: seasonId } }],
  });

  const [prizeModalOpen, setPrizeModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedPrize, setSelectedPrize] = useState<Prize | null>(null);
  const [withCode, setWithCode] = useState(false);

  const handleCreateWithCode = useCallback(() => {
    setSelectedPrize(null);
    setWithCode(true);
    setPrizeModalOpen(true);
  }, []);

  const handleCreateWithoutCode = useCallback(() => {
    setSelectedPrize(null);
    setWithCode(false);
    setPrizeModalOpen(true);
  }, []);

  const handleEditPrize = useCallback((prize: Prize) => {
    setSelectedPrize(prize);
    setWithCode(prize.__typename === 'PrizeWithCode');
    setPrizeModalOpen(true);
  }, []);

  const handleDeleteClick = useCallback((e: React.MouseEvent, prize: Prize) => {
    e.stopPropagation();
    setSelectedPrize(prize);
    setDeleteModalOpen(true);
  }, []);

  const handleCloseModal = useCallback(() => {
    setPrizeModalOpen(false);
    setSelectedPrize(null);
  }, []);

  const handleCloseDeleteModal = useCallback(() => {
    setDeleteModalOpen(false);
    setSelectedPrize(null);
  }, []);

  const handleConfirmDelete = useCallback(async () => {
    if (!selectedPrize) return;

    try {
      await deletePrize({
        variables: {
          input: { id: selectedPrize.id },
        },
      });
      toast.success('Приз успешно удалён');
      handleCloseDeleteModal();
    } catch (error) {
      toast.error('Ошибка при удалении приза');
    }
  }, [selectedPrize, deletePrize, handleCloseDeleteModal]);

  return (
    <>
      <Card>
        <CardHeader
          title="Призы"
          action={
            <Box className={classes.headerActions}>
              <Button
                color="primary"
                variant="contained"
                size="small"
                onClick={handleCreateWithCode}
              >
                Создать приз с кодом
              </Button>
              <Button
                color="primary"
                variant="outlined"
                size="small"
                onClick={handleCreateWithoutCode}
              >
                Создать приз без кода
              </Button>
            </Box>
          }
        />
        <CardContent>
          {prizes.length === 0 ? (
            <Typography className={classes.emptyMessage}>Призы не добавлены</Typography>
          ) : (
            <Grid container spacing={2}>
              {prizes.map((prize) => (
                <Grid item key={prize.id}>
                  <Box className={classes.prizeCard} onClick={() => handleEditPrize(prize)}>
                    <Box className={classes.prizeImageWrapper}>
                      <Avatar
                        src={prize.image.url}
                        variant="square"
                        className={classes.prizeImage}
                      />
                      <Box className={classes.prizeHeader}>
                        <Typography className={classes.prizePrice}>{prize.price}</Typography>
                        <IconButton
                          size="small"
                          onClick={(e) => handleDeleteClick(e, prize)}
                          className={classes.deleteButton}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </Box>
                    <Box className={classes.prizeContent}>
                      <Typography className={classes.prizeTitle}>{prize.title}</Typography>
                      {prize.description && (
                        <Typography className={classes.prizeDescription}>
                          {prize.description}
                        </Typography>
                      )}
                      {prize.__typename === 'PrizeWithCode' && prize.promoCode && (
                        <Typography className={classes.promoCode}>
                          Промокод: <strong>{prize.promoCode}</strong>
                        </Typography>
                      )}
                      <Box className={classes.chipContainer}>
                        {prize.isPremium && (
                          <Chip
                            size="small"
                            icon={<StarIcon />}
                            label="Premium"
                            className={classes.premiumChip}
                          />
                        )}
                        {prize.__typename === 'PrizeWithCode' && (
                          <Chip
                            size="small"
                            icon={<ConfirmationNumberIcon />}
                            label="С промокодом"
                            className={classes.promoChip}
                          />
                        )}
                      </Box>
                    </Box>
                  </Box>
                </Grid>
              ))}
            </Grid>
          )}
        </CardContent>
      </Card>

      <PrizeModal
        open={prizeModalOpen}
        seasonId={seasonId}
        prize={selectedPrize}
        withCode={withCode}
        onClose={handleCloseModal}
      />

      <DeletePrizeModal
        open={deleteModalOpen}
        prizeTitle={selectedPrize?.title || ''}
        onConfirm={handleConfirmDelete}
        onClose={handleCloseDeleteModal}
        loading={deleteLoading}
      />
    </>
  );
};
